import type { Equipment } from '../types/project';

/**
 * Quick-add on the New Loads tab, built from the contractor's own history.
 *
 * TrueLoad does not pre-decide which equipment belongs on the chips
 * (context/ux.md LOCKED #2). Once a person has entered a load — their
 * "Mitsubishi 15A ductless heat pump" — it becomes a one-tap re-add for them,
 * on any project. The list is the most recent distinct entries, newest first.
 */

export type RecentEquipmentRow = Pick<
  Equipment,
  'equipment_name' | 'equipment_type' | 'source' | 'amps' | 'kw' | 'voltage' | 'created_at'
>;

export interface RecentEquipment {
  key: string;
  /** "Mitsubishi 15A ductless heat pump · 15 A" */
  label: string;
  /** What a new row starts with when the chip is pressed. */
  seed: Partial<Equipment>;
  lastUsed: string;
}

/** Chip key → when the contractor removed it. Entering that load again brings it back. */
export type RemovedEquipment = Record<string, string>;

const norm = (s: string | null | undefined) => (s ?? '').trim().replace(/\s+/g, ' ');

function ratingLabel(row: RecentEquipmentRow): string {
  if (row.amps != null && row.amps > 0) return `${row.amps} A`;
  if (row.kw != null && row.kw > 0) return `${row.kw} kW`;
  return '';
}

export function summarizeRecentEquipment(
  rows: RecentEquipmentRow[],
  limit = 8,
  removed: RemovedEquipment = {}
): RecentEquipment[] {
  const sorted = [...rows].sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''));
  const seen = new Set<string>();
  const out: RecentEquipment[] = [];
  for (const row of sorted) {
    if (!row.equipment_type) continue;
    const rated = (row.amps != null && row.amps > 0) || (row.kw != null && row.kw > 0);
    if (!rated) continue;
    const name = norm(row.equipment_name);
    const key = [name.toLowerCase(), row.equipment_type, row.amps ?? '', row.kw ?? '', row.voltage ?? ''].join('|');
    if (seen.has(key)) continue;
    seen.add(key);
    // Rows are newest first, so this is the entry's latest use.
    const removedAt = removed[key];
    if (removedAt && Date.parse(row.created_at ?? '') <= Date.parse(removedAt)) continue;
    out.push({
      key,
      label: `${name || row.equipment_type} · ${ratingLabel(row)}`,
      seed: {
        equipment_name: name,
        equipment_type: row.equipment_type,
        source: row.source ?? 'Nameplate',
        amps: row.amps ?? null,
        kw: row.kw ?? null,
        voltage: row.voltage ?? 240,
        quantity: 1,
      },
      lastUsed: row.created_at ?? '',
    });
    if (out.length >= limit) break;
  }
  return out;
}
